import { inject, DestroyRef, effect, untracked, signal } from '@angular/core';

/**
 * The name of the event that is emitted whenever the storage changes.
 *
 * @see StorageChangeEvent
 */
const STORAGE_CHANGE_EVENT = 'θstorage';
const ALL_KEYS = Symbol('all');
/**
 * A custom event that is emitted whenever the storage changes.
 * This is a shim for the native StorageEvent, which is only emitted when the storage changes from a different window.
 */
class StorageChangeEvent extends CustomEvent {
    constructor(detail) {
        super(STORAGE_CHANGE_EVENT, { detail });
    }
    get key() {
        return this.detail.key;
    }
    get newValue() {
        return this.detail.newValue;
    }
    get oldValue() {
        return this.detail.oldValue;
    }
    get storageArea() {
        return this.detail.storageArea;
    }
}

/**
 * @packageDocumentation
 * A shim that dispatches a custom event whenever the storage changes.
 * We need this because the native StorageEvent is only emitted when the storage changes from a different window.
 *
 * @see CustomStorageEvent
 */
Storage.prototype.setItem = new Proxy(Storage.prototype.setItem, {
    apply(target, thisArg, argumentList) {
        const event = new StorageChangeEvent({
            key: argumentList[0],
            oldValue: thisArg.getItem(argumentList[0]),
            newValue: argumentList[1],
            storageArea: thisArg
        });
        const result = Reflect.apply(target, thisArg, argumentList);
        window.dispatchEvent(event);
        return result;
    }
});
Storage.prototype.removeItem = new Proxy(Storage.prototype.removeItem, {
    apply(target, thisArg, argumentList) {
        const event = new StorageChangeEvent({
            key: argumentList[0],
            oldValue: thisArg.getItem(argumentList[0]),
            storageArea: thisArg
        });
        const result = Reflect.apply(target, thisArg, argumentList);
        window.dispatchEvent(event);
        return result;
    }
});
Storage.prototype.clear = new Proxy(Storage.prototype.clear, {
    apply(target, thisArg, argumentList) {
        const event = new StorageChangeEvent({
            key: ALL_KEYS,
            storageArea: thisArg
        });
        const result = Reflect.apply(target, thisArg, argumentList);
        window.dispatchEvent(event);
        return result;
    }
});

/**
 * @internal
 * Normalizes the options for storage composables.
 *
 * @param options - The options to normalize
 * @returns The normalized options
 */
const normalizeBaseStorageOptions = (options) => ({
    storage: options?.storage ?? localStorage
});

/**
 * @internal
 * Normalizes the options for {@link readFromStorage}.
 *
 * @param options - The options to normalize.
 * @returns The normalized options.
 */
const normalizeReadFromStorageOptions = (options) => {
    return {
        ...normalizeBaseStorageOptions(options),
        skipInitialRead: options?.skipInitialRead ?? false
    };
};
/**
 * Reads the value from the storage into the signal, whenever the storage value changes.
 * This will **not** update the storage when the signal changes.
 *
 * @example
 * ```ts
 * localStorage.setItem('name', 'Jane');
 *
 * const name = signal<string | undefined>(undefined);
 *
 * // will set the value in the signal to 'Jane'
 * readFromStorage('name', name); // name() === 'Jane';
 *
 * // changing the signal directly will *not* update the storage
 * name.set('Alice'); // localStorage.getItem('name') === 'Jane';
 *
 * // changing the value in the storage will update the signal
 * localStorage.setItem('name', 'Mary'); // name() === 'Mary';
 * ```
 *
 * @param key - The key to use for the storage
 * @param value - A signal defining the value to read from the storage
 * @param options - A set of options for the storage
 * @returns The passed in signal (`value` parameter)
 */
const readFromStorage = (key, value, options) => {
    const { storage, skipInitialRead } = normalizeReadFromStorageOptions(options);
    const destroyRef = inject(DestroyRef);
    if (!skipInitialRead) {
        value.set(storage.getItem(key));
    }
    const listener = (event) => {
        if (!(event instanceof StorageChangeEvent) ||
            event.storageArea !== storage ||
            (event.key !== key && event.key !== ALL_KEYS)) {
            return;
        }
        value.set(event.newValue);
    };
    window.addEventListener(STORAGE_CHANGE_EVENT, listener);
    destroyRef.onDestroy(() => window.removeEventListener(STORAGE_CHANGE_EVENT, listener));
    return value;
};

/**
 * @internal
 * Normalizes the options for {@link writeToStorage}.
 *
 * @param options - The options to normalize
 * @returns The normalized options
 */
const normalizeWriteToStorageOptions = (options) => normalizeBaseStorageOptions(options);
/**
 * Writes the value of the given signal to the storage whenever it changes.
 * This will **not** update the signal when the value in the storage changes.
 *
 * @example
 * ```ts
 * const name = signal('Jane');
 *
 * // will set the value in the storage to 'Jane', even if it was previously set to something else
 * writeToStorage('name', name); // localStorage.getItem('name') === 'Jane';
 *
 * // changing the value in the storage will **not** update the signal
 * localStorage.setItem('name', 'Alice'); // name() === 'Jane';
 *
 * // changing the signal directly will update the storage
 * name.set('Mary'); // localStorage.getItem('name') === 'Mary';
 * ```
 *
 * @param key - The key to use for the storage
 * @param value - A signal defining the value to write to the storage
 * @param options - A set of options for the storage
 * @returns The passed in signal (`value` parameter)
 */
const writeToStorage = (key, value, options) => {
    const { storage } = normalizeWriteToStorageOptions(options);
    effect(() => {
        const currentValue = value();
        untracked(() => {
            if (currentValue != null) {
                storage.setItem(key, currentValue);
            }
            else {
                storage.removeItem(key);
            }
        });
    });
    return value;
};

/**
 * @internal
 * Normalizes the options for {@link bindStorage}.
 *
 * @param options - The options to normalize
 * @returns The normalized options
 */
const normalizeBindStorageOptions = (options) => normalizeReadFromStorageOptions(options);
/**
 * Binds the given signal to the storage.
 * When the value of the signal changes, it will be written to the storage.
 * When the value in the storage changes, the signal will be updated.
 *
 * @example
 * ```ts
 * const name = signal('Jane');
 *
 * // will set the value in the storage to 'Jane', even if it was previously set to something else
 * bindStorage('name', name); // localStorage.getItem('name') === 'Jane';
 *
 * // changing the signal directly will update the storage
 * name.set('Alice'); // localStorage.getItem('name') === 'Alice';
 *
 * // changing the value in the storage will update the signal
 * localStorage.setItem('name', 'Mary'); // name() === 'Mary';
 * ```
 *
 * @param key - The key to use for the storage
 * @param value - The signal to bind to the storage
 * @param options - A set of options for the storage
 * @returns The passed in signal (`value` parameter)
 */
const bindStorage = (key, value, options) => {
    return readFromStorage(key, writeToStorage(key, value, options), options);
};

/**
 * @internal
 * Normalizes the options for {@link useStorage}.
 *
 * @param key - The key to use for the storage
 * @param options - The options to normalize
 * @returns The normalized options
 */
const normalizeUseStorageOptions = (key, options) => {
    const baseStorageOptions = normalizeBindStorageOptions(options);
    return {
        ...baseStorageOptions,
        initialValue: options?.initialValue ?? baseStorageOptions.storage.getItem(key)
    };
};
/**
 * Creates a signal that is synced with the given storage.
 * When the value of the signal changes, it will be written to the storage.
 * When the value in the storage changes, the signal will be updated.
 *
 * @example
 * ```ts
 * // will set the value in the storage to 'Jane', even if it was previously set to something else
 * const name = useStorage('name', { initialValue: 'Jane' }); // localStorage.getItem('name') === 'Jane';
 *
 * // changing the signal directly will update the storage
 * name.set('Alice'); // localStorage.getItem('name') === 'Alice';
 *
 * // changing the value in the storage will update the signal
 * localStorage.setItem('name', 'Mary'); // name() === 'Mary';
 * ```
 *
 * @param key - The key to use for the storage
 * @param options - A set of options for the storage
 * @returns A signal that is synced with the given storage
 */
const useStorage = (key, options) => {
    const { storage, initialValue } = normalizeUseStorageOptions(key, options);
    return bindStorage(key, signal(initialValue), { storage });
};

/**
 * @packageDocumentation
 * Composables for reading and writing data to the storage of the browser.
 *
 * @module @bynary/composables/storage
 */

/**
 * Generated bundle index. Do not edit.
 */

export { bindStorage, useStorage };
